import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API_BASE } from "@/App";
import { ChevronRight, Target, Clock, CheckCircle } from "lucide-react";

/**
 * CLARIAS Intelligence Check
 * ≤60 seconds to classify user and identify biggest risk
 * 
 * Questions:
 * 1. Target attempt year
 * 2. Attempt type (First-time / Repeater)
 * 3. Prelims cleared before (Yes / No)
 * 4. CSAT confidence (Safe / Borderline / Unsafe)
 * 5. Daily study time
 */

const IntelligenceCheckPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({
    target_year: "",
    attempt_type: "",
    prelims_cleared: null,
    csat_confidence: "",
    daily_study_hours: ""
  });

  const questions = [
    {
      id: "target_year",
      question: "Which year are you targeting?",
      subtitle: "When do you plan to appear for UPSC CSE?",
      options: [
        { value: "2025", label: "UPSC 2025", sublabel: "This year" },
        { value: "2026", label: "UPSC 2026", sublabel: "Next year" },
        { value: "2027", label: "UPSC 2027", sublabel: "2+ years away" }
      ]
    },
    {
      id: "attempt_type",
      question: "Is this your first attempt?",
      subtitle: "Your preparation strategy depends on this",
      options: [
        { value: "first_time", label: "First Attempt", sublabel: "Never appeared before" },
        { value: "repeater", label: "Repeater", sublabel: "Appeared at least once" }
      ]
    },
    {
      id: "prelims_cleared",
      question: "Have you cleared Prelims before?",
      subtitle: "This helps us understand your GS strength",
      options: [
        { value: true, label: "Yes", sublabel: "Cleared at least once" },
        { value: false, label: "No", sublabel: "Not yet cleared" }
      ]
    },
    {
      id: "csat_confidence",
      question: "How confident are you in CSAT?",
      subtitle: "Be honest. CSAT failure is catastrophic.",
      options: [
        { value: "safe", label: "🟢 Safe", sublabel: "Consistently score 100+" },
        { value: "borderline", label: "🟡 Borderline", sublabel: "Score between 80-100" },
        { value: "unsafe", label: "🔴 Unsafe", sublabel: "Often score below 80" }
      ]
    },
    {
      id: "daily_study_hours",
      question: "How many hours can you study daily?",
      subtitle: "Realistic estimate, not aspirational",
      options: [
        { value: "2-4", label: "2-4 hours", sublabel: "Part-time preparation" },
        { value: "4-6", label: "4-6 hours", sublabel: "Moderate focus" },
        { value: "6-8", label: "6-8 hours", sublabel: "Serious preparation" },
        { value: "8+", label: "8+ hours", sublabel: "Full-time dedication" }
      ]
    }
  ];

  const currentQuestion = questions[step];

  const handleSelect = async (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);

    if (step < questions.length - 1) {
      // Move to next question
      setTimeout(() => setStep(step + 1), 300);
    } else {
      // Submit intelligence check
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await managedAxios.post(
          `${API_BASE}/command/intelligence-check`,
          newAnswers,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Navigate to command screen
        navigate("/command", { 
          state: { 
            command: response.data.command,
            forced_action: response.data.forced_action 
          }
        });
      } catch (error) {
        console.error("Error submitting intelligence check:", error);
        // Still navigate, will fetch command on that page
        navigate("/command");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-xl">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {questions.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                idx < step
                  ? "w-8 bg-emerald-500"
                  : idx === step
                  ? "w-12 bg-blue-500"
                  : "w-8 bg-slate-700"
              }`}
            />
          ))}
        </div>

        {/* Timer hint */}
        <div className="flex items-center justify-center gap-2 mb-4 text-slate-500">
          <Clock className="w-4 h-4" />
          <span className="text-sm">≤60 seconds</span>
        </div>

        {/* Question Card */}
        <Card className="border-0 bg-slate-800/50 backdrop-blur-xl shadow-2xl">
          <CardContent className="p-8">
            {/* Question */}
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {currentQuestion.question}
              </h1>
              <p className="text-slate-400">
                {currentQuestion.subtitle}
              </p>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value?.toString()}
                  onClick={() => handleSelect(option.value)}
                  disabled={loading}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left group
                    ${answers[currentQuestion.id] === option.value
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-slate-700 hover:border-slate-600 hover:bg-slate-700/50"
                    }
                    ${loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white text-lg">
                        {option.label}
                      </p>
                      <p className="text-sm text-slate-400">
                        {option.sublabel}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all ${
                      answers[currentQuestion.id] === option.value ? "text-blue-400" : ""
                    }`} />
                  </div>
                </button>
              ))}
            </div>

            {/* Loading state */}
            {loading && (
              <div className="mt-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 rounded-full text-blue-400">
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  <span>Analyzing your profile...</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-slate-500 text-sm">
            Question {step + 1} of {questions.length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default IntelligenceCheckPage;
